import { useCallback, useRef } from 'react';
import type * as THREE from 'three';
import { axialToWorld, type HexTileCoordinates, hexMapConfig } from '@/game/hexmap';
import { isCity } from '@/game/sim';
import type { FrameStore } from '@/game/view/frame';
import type { CameraView, StrategyCameraHandle } from '../StrategyCamera';

/**
 * The strategy camera's handle and the pans the HUD asks of it. The camera
 * centres on the player's starting city once it exists, and keeps its view
 * across a remount of the canvas (a quality change makes a new WebGL context).
 */
export function useCameraControl(frames: FrameStore) {
  const cameraRef = useRef<StrategyCameraHandle | null>(null);
  // The Three camera itself, for the edge arrows' and minimap's projections.
  const viewCameraRef = useRef<THREE.Camera | null>(null);
  const panToWorld = useCallback((x: number, z: number) => {
    cameraRef.current?.panTo(x, z);
  }, []);
  const panTo = useCallback((pos: HexTileCoordinates) => {
    const world = axialToWorld(pos, hexMapConfig.hexMap.tile.radius);
    cameraRef.current?.panTo(world.x, world.z);
  }, []);

  // The Canvas mounts its children after the screen's effects run, so
  // centring hangs off the camera ref rather than a mount effect.
  const centeredRef = useRef(false);
  // The view as the Canvas unmounts, restored on its remount.
  const savedViewRef = useRef<CameraView | null>(null);
  const setCamera = useCallback(
    (controls: StrategyCameraHandle | null) => {
      if (!controls) {
        if (cameraRef.current) savedViewRef.current = cameraRef.current.getView();
        cameraRef.current = null;
        return;
      }
      cameraRef.current = controls;
      if (savedViewRef.current) {
        controls.setView(savedViewRef.current);
        savedViewRef.current = null;
        return;
      }
      if (centeredRef.current) return;
      const { view, playerId, rules } = frames.get();
      const city = Object.values(view.buildings).find(
        (b) => b.owner === playerId && isCity(b, rules)
      );
      if (!city) return;
      centeredRef.current = true;
      panTo(city.pos);
    },
    [frames, panTo]
  );

  return { setCamera, viewCameraRef, panTo, panToWorld };
}
